import { t, Trans } from "@lingui/macro";
import {
  Briefcase,
  ChartLineUp,
  ChatCircleDots,
  CheckCircle,
  DownloadSimple,
  EnvelopeOpen,
  FileArrowDown,
  FireSimple,
  Globe,
  Microphone,
  Palette,
  Stack,
  Target,
} from "@phosphor-icons/react";
import { Button } from "@stylingcv/ui";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

type Feature = {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  preview: React.ReactNode;
  accent: string;
  fullWidth?: boolean;
};

/* ------------------------- Card preview components ------------------------ */

const AIAgentPreview = () => (
  <div className="flex h-full flex-col gap-3 p-5">
    <div className="flex items-start gap-2.5">
      <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-teal/20 text-[10px] font-bold text-brand-teal">
        AI
      </div>
      <div className="rounded-2xl rounded-tl-sm bg-white/[0.06] px-3.5 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
        Hi, I need help creating the best resume possible. Where should I start?
      </div>
    </div>
    <div className="flex items-start gap-2.5">
      <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-[10px] font-bold text-muted-foreground">
        Y
      </div>
      <div className="rounded-2xl rounded-tl-sm bg-white/[0.04] px-3.5 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
        I have one but it&apos;s pretty basic — needs work.
      </div>
    </div>
    <div className="flex items-start gap-2.5">
      <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-teal/20 text-[10px] font-bold text-brand-teal">
        AI
      </div>
      <div className="rounded-2xl rounded-tl-sm bg-brand-teal/10 px-3.5 py-2.5 text-[11px] leading-relaxed text-muted-foreground ring-1 ring-brand-teal/20">
        Got it. Let&apos;s tailor it to your target role first.
      </div>
    </div>
  </div>
);

const JobTrackerPreview = () => (
  <div className="flex h-full flex-col gap-2.5 p-5">
    <div className="flex items-center justify-between gap-2 rounded-lg bg-white/5 px-3 py-2 ring-1 ring-white/10">
      <div className="flex items-center gap-2">
        <div className="size-1.5 rounded-full bg-blue-400" />
        <span className="text-[11px] font-medium text-muted-foreground">Senior SWE — Apple</span>
      </div>
      <span className="rounded-md bg-blue-500/15 px-1.5 py-0.5 text-[9px] font-medium text-blue-300">
        Applied
      </span>
    </div>
    <div className="flex items-center justify-between gap-2 rounded-lg bg-white/5 px-3 py-2 ring-1 ring-white/10">
      <div className="flex items-center gap-2">
        <div className="size-1.5 rounded-full bg-amber-400" />
        <span className="text-[11px] font-medium text-muted-foreground">Mobile Dev — Capital One</span>
      </div>
      <span className="rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-medium text-amber-300">
        Interview
      </span>
    </div>
    <div className="flex items-center justify-between gap-2 rounded-lg bg-white/5 px-3 py-2 ring-1 ring-white/10">
      <div className="flex items-center gap-2">
        <div className="size-1.5 rounded-full bg-emerald-400" />
        <span className="text-[11px] font-medium text-muted-foreground">ML Engineer — Adobe</span>
      </div>
      <span className="rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-medium text-emerald-300">
        Offer
      </span>
    </div>
    <div className="flex items-center justify-between gap-2 rounded-lg bg-white/5 px-3 py-2 ring-1 ring-white/10">
      <div className="flex items-center gap-2">
        <div className="size-1.5 rounded-full bg-white/40" />
        <span className="text-[11px] font-medium text-muted-foreground">Data Scientist — Meta</span>
      </div>
      <span className="rounded-md bg-white/10 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">
        Saved
      </span>
    </div>
  </div>
);

const InterviewPreview = () => (
  <div className="flex h-full flex-col items-center justify-center gap-3 p-5">
    <div className="relative">
      <div className="absolute inset-0 animate-ping rounded-full bg-brand-teal/20" />
      <div className="relative flex size-14 items-center justify-center rounded-full bg-gradient-to-br from-brand-teal to-brand-teal/60 shadow-lg shadow-brand-teal/30">
        <Microphone weight="fill" className="size-6 text-white" />
      </div>
    </div>
    <div className="flex items-end gap-1">
      {[8, 14, 22, 16, 10, 18, 24, 14, 8].map((h, i) => (
        <div
          key={i}
          className="w-1 rounded-full bg-brand-teal/60"
          style={{ height: `${h}px`, animation: `pulse 1.4s ease-in-out ${i * 0.1}s infinite` }}
        />
      ))}
    </div>
    <div className="rounded-full bg-white/5 px-3 py-1 text-[10px] font-medium text-muted-foreground ring-1 ring-white/10">
      Question 3 of 5 — STAR method
    </div>
  </div>
);

const KeywordsPreview = () => (
  <div className="flex h-full flex-col gap-2.5 p-5">
    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
      Missing keywords
    </div>
    {[
      { label: "Marketing Strategy", on: true },
      { label: "Analytics", on: true },
      { label: "Customer Segmentation", on: true },
      { label: "A/B Testing", on: false },
    ].map((k) => (
      <div
        key={k.label}
        className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2 ring-1 ring-white/10"
      >
        <span className="text-[11px] font-medium text-muted-foreground">{k.label}</span>
        {k.on ? (
          <CheckCircle weight="fill" className="size-4 text-emerald-400" />
        ) : (
          <div className="size-3.5 rounded-full ring-1 ring-white/30" />
        )}
      </div>
    ))}
  </div>
);

const MarketIntelPreview = () => (
  <div className="flex h-full flex-col gap-3 p-5">
    <div className="flex items-baseline justify-between">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
        Market DNA
      </span>
      <span className="text-[11px] font-bold text-brand-teal">+42%</span>
    </div>
    <div className="relative h-16">
      <svg viewBox="0 0 200 60" className="absolute inset-0 size-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="mr-grad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgb(20 184 166)" stopOpacity="0.4" />
            <stop offset="100%" stopColor="rgb(20 184 166)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0,45 L25,40 L50,42 L75,30 L100,32 L125,20 L150,22 L175,12 L200,8 L200,60 L0,60 Z"
          fill="url(#mr-grad)"
        />
        <path
          d="M0,45 L25,40 L50,42 L75,30 L100,32 L125,20 L150,22 L175,12 L200,8"
          fill="none"
          stroke="rgb(20 184 166)"
          strokeWidth="1.5"
        />
      </svg>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {["React", "TypeScript", "AWS", "GraphQL"].map((tag) => (
        <span
          key={tag}
          className="rounded-md bg-brand-teal/10 px-2 py-0.5 text-[10px] font-medium text-brand-teal ring-1 ring-brand-teal/20"
        >
          {tag}
        </span>
      ))}
    </div>
  </div>
);

const TEMPLATE_THUMBS = [
  "/templates/png/pinnacle.png",
  "/templates/png/modern-corporate.png",
  "/templates/png/executive-neat.png",
  "/templates/png/aura.png",
  "/templates/png/minimalist-elite.png",
  "/templates/png/onyx.png",
];

const TemplatesPreview = () => (
  <div className="grid h-full grid-cols-3 gap-2 p-4">
    {TEMPLATE_THUMBS.map((src, i) => (
      <div
        key={src}
        className="relative overflow-hidden rounded-md bg-white/5 shadow-lg ring-1 ring-white/10 transition-transform duration-500 hover:-translate-y-1"
        style={{ transform: `rotate(${(i % 2 === 0 ? -1 : 1) * 1.5}deg)` }}
      >
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          className="size-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
      </div>
    ))}
  </div>
);

const StylionPreview = () => (
  <div className="relative flex h-full items-center justify-center overflow-hidden p-4">
    <div className="absolute left-1/2 top-1/2 size-32 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500/20 blur-3xl" />
    <img
      src="/stylion/expressions/stylion-hero.png"
      alt="Stylion the resume roaster"
      loading="lazy"
      decoding="async"
      className="relative size-32 object-contain drop-shadow-[0_8px_24px_rgba(245,158,11,0.25)]"
    />
    <div className="absolute right-3 top-3 max-w-[55%] rounded-2xl rounded-br-sm bg-white px-3 py-2 text-[11px] font-medium leading-snug text-gray-800 shadow-xl">
      &ldquo;Your resume is mid. Let me fix it.&rdquo;
      <div className="absolute -bottom-1 right-3 size-2 rotate-45 bg-white" />
    </div>
    <div className="absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-red-500/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-lg">
      <FireSimple weight="fill" className="size-3" />
      Roasted
    </div>
  </div>
);

const AtsScorePreview = () => {
  const score = 94;
  const circumference = 2 * Math.PI * 32;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="flex h-full items-center gap-4 p-5">
      <div className="relative shrink-0">
        <svg viewBox="0 0 80 80" className="size-20 -rotate-90">
          <circle cx="40" cy="40" r="32" stroke="rgb(255 255 255 / 0.08)" strokeWidth="6" fill="none" />
          <circle
            cx="40"
            cy="40"
            r="32"
            stroke="url(#ats-grad)"
            strokeWidth="6"
            strokeLinecap="round"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
          <defs>
            <linearGradient id="ats-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="rgb(20 184 166)" />
              <stop offset="100%" stopColor="rgb(34 197 94)" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-bold text-white">{score}</span>
          <span className="text-[8px] font-semibold uppercase tracking-wider text-muted-foreground/80">
            ATS Score
          </span>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-2">
        {[
          { label: "Keywords", value: 96 },
          { label: "Format", value: 92 },
          { label: "Readability", value: 88 },
        ].map((row) => (
          <div key={row.label}>
            <div className="mb-1 flex items-center justify-between text-[10px]">
              <span className="font-medium text-muted-foreground">{row.label}</span>
              <span className="font-bold text-emerald-400">{row.value}</span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-white/5">
              <div
                className="h-full rounded-full bg-gradient-to-r from-brand-teal to-emerald-400"
                style={{ width: `${row.value}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AIDesignerPreview = () => (
  <div className="flex h-full flex-col gap-3 p-5">
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
        Theme
      </span>
      <span className="rounded-md bg-pink-500/15 px-1.5 py-0.5 text-[9px] font-bold text-pink-300">
        AI Generated
      </span>
    </div>
    <div className="flex gap-1.5">
      {[
        "bg-rose-500",
        "bg-pink-500",
        "bg-fuchsia-500",
        "bg-purple-500",
        "bg-violet-500",
        "bg-indigo-500",
      ].map((c, i) => (
        <div
          key={i}
          className={`size-7 rounded-full ${c} ring-2 ${
            i === 1 ? "ring-white" : "ring-white/10"
          }`}
        />
      ))}
    </div>
    <div className="space-y-2 rounded-lg bg-white/5 p-3 ring-1 ring-white/10">
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-muted-foreground">Font</span>
        <span className="font-medium text-foreground/90">Inter</span>
      </div>
      <div className="h-px bg-white/5" />
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-muted-foreground">Spacing</span>
        <div className="flex h-1 w-16 items-center rounded-full bg-white/10">
          <div className="h-full w-2/3 rounded-full bg-pink-400" />
        </div>
      </div>
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-muted-foreground">Radius</span>
        <div className="flex h-1 w-16 items-center rounded-full bg-white/10">
          <div className="h-full w-1/2 rounded-full bg-pink-400" />
        </div>
      </div>
    </div>
  </div>
);

const LANGUAGES = [
  { flag: "🇺🇸", code: "EN" },
  { flag: "🇸🇦", code: "AR" },
  { flag: "🇫🇷", code: "FR" },
  { flag: "🇩🇪", code: "DE" },
  { flag: "🇪🇸", code: "ES" },
  { flag: "🇯🇵", code: "JA" },
  { flag: "🇨🇳", code: "ZH" },
  { flag: "🇰🇷", code: "KO" },
  { flag: "🇮🇹", code: "IT" },
  { flag: "🇧🇷", code: "PT" },
  { flag: "🇮🇳", code: "HI" },
  { flag: "🇹🇷", code: "TR" },
];

const TranslatePreview = () => (
  <div className="flex h-full flex-col gap-3 p-5">
    <div className="grid grid-cols-6 gap-1.5">
      {LANGUAGES.map((lang, i) => (
        <div
          key={lang.code}
          className={`flex aspect-square items-center justify-center rounded-md text-base ring-1 transition ${
            i === 1
              ? "bg-cyan-500/15 ring-cyan-400/40"
              : "bg-white/5 ring-white/10"
          }`}
          title={lang.code}
        >
          {lang.flag}
        </div>
      ))}
    </div>
    <div className="flex items-center gap-2 rounded-lg bg-cyan-500/10 px-3 py-2 ring-1 ring-cyan-400/20">
      <Globe weight="fill" className="size-4 text-cyan-300" />
      <span className="flex-1 text-[11px] font-medium text-foreground/90">
        Translating EN → AR
      </span>
      <span className="text-[10px] font-bold text-cyan-300">RTL ✓</span>
    </div>
  </div>
);

const ExportPreview = () => (
  <div className="flex h-full flex-col items-center justify-center gap-3 p-5">
    <div className="flex items-end gap-3">
      <motion.div
        initial={{ rotate: -8, y: 4 }}
        animate={{ rotate: -8, y: [4, -2, 4] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        className="flex flex-col items-center gap-1.5"
      >
        <div className="relative">
          <FileArrowDown weight="fill" className="size-12 text-red-400" />
          <span className="absolute -bottom-0.5 right-0 rounded bg-red-500 px-1 py-0.5 text-[7px] font-bold text-white">
            PDF
          </span>
        </div>
        <span className="text-[9px] font-medium text-muted-foreground">High-fidelity</span>
      </motion.div>
      <motion.div
        initial={{ rotate: 8, y: 0 }}
        animate={{ rotate: 8, y: [0, -6, 0] }}
        transition={{ duration: 3, delay: 0.5, repeat: Infinity, ease: "easeInOut" }}
        className="flex flex-col items-center gap-1.5"
      >
        <div className="relative">
          <FileArrowDown weight="fill" className="size-12 text-blue-400" />
          <span className="absolute -bottom-0.5 right-0 rounded bg-blue-500 px-1 py-0.5 text-[7px] font-bold text-white">
            DOCX
          </span>
        </div>
        <span className="text-[9px] font-medium text-muted-foreground">Editable</span>
      </motion.div>
    </div>
    <div className="flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 ring-1 ring-white/10">
      <DownloadSimple className="size-3 text-muted-foreground" />
      <span className="text-[10px] font-medium text-muted-foreground">Pixel-perfect output</span>
    </div>
  </div>
);

const ShareAndPwaPreview = () => (
  <div className="flex h-full flex-col gap-3 p-5">
    <div className="flex items-center gap-2 rounded-lg bg-white/5 px-2.5 py-2 ring-1 ring-white/10">
      <div className="flex gap-1">
        <div className="size-1.5 rounded-full bg-red-400/60" />
        <div className="size-1.5 rounded-full bg-amber-400/60" />
        <div className="size-1.5 rounded-full bg-emerald-400/60" />
      </div>
      <div className="flex-1 truncate font-mono text-[10px] text-muted-foreground">
        ai.stylion.io<span className="text-brand-teal">/yasser/senior-engineer</span>
      </div>
      <CheckCircle weight="fill" className="size-3.5 text-emerald-400" />
    </div>

    <div className="grid grid-cols-3 gap-3">
      {/* QR Code mockup */}
      <div className="grid grid-cols-5 gap-0.5 rounded-md bg-white p-1.5">
        {Array.from({ length: 25 }, (_, i) => {
          const filled = [0, 1, 2, 3, 4, 5, 9, 10, 14, 15, 19, 20, 21, 22, 23, 24, 6, 8, 12, 18].includes(
            i,
          );
          return (
            <div
              key={i}
              className={`aspect-square ${filled ? "bg-gray-900" : "bg-white"}`}
            />
          );
        })}
      </div>

      <div className="col-span-2 flex flex-col justify-center gap-1.5">
        <div className="flex items-center gap-1.5">
          <div className="flex size-5 items-center justify-center rounded bg-brand-teal/20 text-[8px]">
            📱
          </div>
          <span className="text-[10px] font-medium text-muted-foreground">Install as PWA</span>
        </div>
        <div className="flex items-center gap-1.5">
          <CheckCircle weight="fill" className="size-3.5 text-emerald-400" />
          <span className="text-[10px] font-medium text-muted-foreground">Works offline</span>
        </div>
        <div className="flex items-center gap-1.5">
          <CheckCircle weight="fill" className="size-3.5 text-emerald-400" />
          <span className="text-[10px] font-medium text-muted-foreground">Cross-device sync</span>
        </div>
      </div>
    </div>
  </div>
);

const CoverLetterPreview = () => (
  <div className="grid h-full grid-cols-1 md:grid-cols-2">
    <div className="relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-white/[0.03] to-transparent p-6">
      <div className="absolute -bottom-6 -right-6 size-40 rounded-full bg-fuchsia-500/10 blur-3xl" />
      <motion.div
        initial={{ rotate: -2, y: 10, opacity: 0 }}
        whileInView={{ rotate: -2, y: 0, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="relative aspect-[3/4] w-full max-w-[200px] rounded-md bg-white p-4 shadow-2xl ring-1 ring-black/5"
      >
        <div className="mb-3 flex items-start gap-2">
          <div className="size-6 rounded-full bg-gradient-to-br from-fuchsia-400 to-fuchsia-600" />
          <div className="flex-1">
            <div className="h-1.5 w-3/4 rounded-sm bg-gray-800" />
            <div className="mt-1 h-1 w-1/2 rounded-sm bg-gray-300" />
          </div>
        </div>
        <div className="mb-2 h-1 w-1/3 rounded-sm bg-gray-300" />
        <div className="space-y-1">
          {[100, 95, 88, 92, 70].map((w, i) => (
            <div key={i} className="h-1 rounded-sm bg-gray-200" style={{ width: `${w}%` }} />
          ))}
        </div>
        <div className="mt-3 space-y-1">
          {[85, 78, 65].map((w, i) => (
            <div key={i} className="h-1 rounded-sm bg-gray-200" style={{ width: `${w}%` }} />
          ))}
        </div>
        <div className="mt-3 space-y-1">
          <div className="h-1 w-[40%] rounded-sm bg-gray-300" />
          <svg viewBox="0 0 60 12" className="h-3 w-12">
            <path
              d="M2 6 Q 8 2, 14 6 T 26 6 T 38 6 T 50 6"
              stroke="rgb(192 38 211)"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </motion.div>
    </div>
    <div className="hidden flex-col justify-center gap-3 p-6 md:flex">
      <div className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2 ring-1 ring-white/10">
        <CheckCircle weight="fill" className="size-4 text-fuchsia-400" />
        <span className="text-[11px] font-medium text-muted-foreground">Tailored to job description</span>
      </div>
      <div className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2 ring-1 ring-white/10">
        <CheckCircle weight="fill" className="size-4 text-fuchsia-400" />
        <span className="text-[11px] font-medium text-muted-foreground">Matches your resume tone</span>
      </div>
      <div className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2 ring-1 ring-white/10">
        <CheckCircle weight="fill" className="size-4 text-fuchsia-400" />
        <span className="text-[11px] font-medium text-muted-foreground">15+ languages</span>
      </div>
    </div>
  </div>
);

/* ----------------------------- Card component ----------------------------- */

type FeatureCardProps = {
  feature: Feature;
  index: number;
  isActive: boolean;
  reduceMotion: boolean;
  registerRef: (el: HTMLElement | null) => void;
};

const FeatureCard = ({ feature, index, isActive, reduceMotion, registerRef }: FeatureCardProps) => {
  const ref = useRef<HTMLElement | null>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const isOdd = index % 2 === 1;
  const shouldParallax = isOdd && !reduceMotion && !feature.fullWidth;
  const y = useTransform(scrollYProgress, [0, 1], shouldParallax ? [40, -40] : [0, 0]);

  return (
    <motion.article
      ref={(el) => {
        ref.current = el;
        registerRef(el);
      }}
      style={{ y }}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: (index % 2) * 0.1 }}
      className={`group relative overflow-hidden rounded-2xl border border-border bg-white/[0.02] backdrop-blur-sm transition-[border,background,box-shadow] duration-300 hover:border-white/20 hover:bg-white/[0.04] ${
        feature.fullWidth ? "sm:col-span-2" : ""
      } ${
        isActive
          ? "border-brand-teal/30 shadow-[0_0_40px_-10px_rgba(20,184,166,0.4)]"
          : ""
      }`}
    >
      <div
        className={`relative overflow-hidden bg-gradient-to-br ${feature.accent} border-b border-border ${
          feature.fullWidth ? "aspect-[16/7]" : "aspect-[4/3]"
        }`}
      >
        {feature.preview}
      </div>
      <div className="p-5">
        <div className="mb-2 flex items-center gap-2">
          <div className="flex size-7 items-center justify-center rounded-lg bg-white/5 text-brand-teal ring-1 ring-white/10">
            {feature.icon}
          </div>
          <h3 className="text-base font-semibold text-white">{feature.title}</h3>
        </div>
        <p
          className={`text-sm leading-relaxed text-white/55 ${
            feature.fullWidth ? "max-w-2xl" : ""
          }`}
        >
          {feature.description}
        </p>
      </div>
      <div className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-brand-teal/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
    </motion.article>
  );
};

/* -------------------------------- Section --------------------------------- */

export const MoreReasonsSection = () => {
  const reduceMotion = useReducedMotion() ?? false;

  const features: Feature[] = [
    {
      id: "ai-agent",
      title: t`AI Resume Agent`,
      description: t`Chat to rewrite summaries, bullets, or full sections so you ship a stronger resume without staring at a blank page.`,
      icon: <ChatCircleDots weight="fill" className="size-4" />,
      preview: <AIAgentPreview />,
      accent: "from-brand-teal/20 via-brand-teal/5 to-transparent",
    },
    {
      id: "job-tracker",
      title: t`Job Search & Tracking`,
      description: t`Track every application in one board and tailor resumes per job so you never lose momentum in your search.`,
      icon: <Briefcase weight="fill" className="size-4" />,
      preview: <JobTrackerPreview />,
      accent: "from-blue-500/20 via-blue-500/5 to-transparent",
    },
    {
      id: "ai-interview",
      title: t`AI Voice Interview`,
      description: t`Practice STAR answers matched to your role so you walk into interviews prepared—not guessing.`,
      icon: <Microphone weight="fill" className="size-4" />,
      preview: <InterviewPreview />,
      accent: "from-rose-500/20 via-rose-500/5 to-transparent",
    },
    {
      id: "ats-score",
      title: t`Real ATS Score Checker`,
      description: t`See your 0–100 ATS fit and what to fix so more applications reach a human recruiter.`,
      icon: <Target weight="fill" className="size-4" />,
      preview: <AtsScorePreview />,
      accent: "from-emerald-500/20 via-emerald-500/5 to-transparent",
    },
    {
      id: "keywords",
      title: t`AI Keyword Targeting`,
      description: t`Spot missing keywords from any job post so you add them in the right places before you apply.`,
      icon: <CheckCircle weight="fill" className="size-4" />,
      preview: <KeywordsPreview />,
      accent: "from-cyan-500/20 via-cyan-500/5 to-transparent",
    },
    {
      id: "market-intel",
      title: t`Market Intelligence`,
      description: t`Research what employers want for your role and market so every line targets real hiring signals—not guesswork.`,
      icon: <ChartLineUp weight="fill" className="size-4" />,
      preview: <MarketIntelPreview />,
      accent: "from-violet-500/20 via-violet-500/5 to-transparent",
    },
    {
      id: "ai-designer",
      title: t`AI Designer`,
      description: t`Generate on-brand colors and layout in seconds so your resume looks intentional without hiring a designer.`,
      icon: <Palette weight="fill" className="size-4" />,
      preview: <AIDesignerPreview />,
      accent: "from-pink-500/20 via-pink-500/5 to-transparent",
    },
    {
      id: "translate",
      title: t`15+ Languages with One-Click Translate`,
      description: t`One-click translate with RTL and local formatting so you can apply across borders without starting over.`,
      icon: <Globe weight="fill" className="size-4" />,
      preview: <TranslatePreview />,
      accent: "from-cyan-500/20 via-cyan-500/5 to-transparent",
    },
    {
      id: "stylion",
      title: t`Stylion — AI Resume Roaster`,
      description: t`Get blunt, actionable critique so you fix what actually hurts callbacks—not just generic tips.`,
      icon: <FireSimple weight="fill" className="size-4" />,
      preview: <StylionPreview />,
      accent: "from-amber-500/20 via-orange-500/5 to-transparent",
    },
    {
      id: "templates",
      title: t`56 Professional Templates`,
      description: t`Pick ATS-safe layouts that update live as you type so you finish faster and still look premium.`,
      icon: <Stack weight="fill" className="size-4" />,
      preview: <TemplatesPreview />,
      accent: "from-indigo-500/20 via-indigo-500/5 to-transparent",
    },
    {
      id: "export",
      title: t`PDF & DOCX Export`,
      description: t`Export print-ready PDFs and editable Word files so you meet any employer's submission rules.`,
      icon: <DownloadSimple weight="fill" className="size-4" />,
      preview: <ExportPreview />,
      accent: "from-red-500/15 via-red-500/5 to-transparent",
    },
    {
      id: "share-pwa",
      title: t`Public Sharing & PWA`,
      description: t`Share a live resume link and install offline access so recruiters always see your latest version.`,
      icon: <Globe weight="fill" className="size-4" />,
      preview: <ShareAndPwaPreview />,
      accent: "from-teal-500/15 via-teal-500/5 to-transparent",
    },
    {
      id: "cover-letter",
      title: t`AI Cover Letter Generator`,
      description: t`Generate cover letters matched to each job so your application feels complete and personalized.`,
      icon: <EnvelopeOpen weight="fill" className="size-4" />,
      preview: <CoverLetterPreview />,
      accent: "from-fuchsia-500/15 via-fuchsia-500/5 to-transparent",
      fullWidth: true,
    },
  ];

  const [activeIndex, setActiveIndex] = useState(0);
  const cardRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    if (reduceMotion) return;
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        let bestEntry: IntersectionObserverEntry | null = null;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (!bestEntry || entry.intersectionRatio > bestEntry.intersectionRatio) {
            bestEntry = entry;
          }
        }
        if (bestEntry) {
          const idx = cardRefs.current.findIndex((el) => el === bestEntry!.target);
          if (idx !== -1) setActiveIndex(idx);
        }
      },
      {
        rootMargin: "-30% 0px -45% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    cardRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [reduceMotion, features.length]);

  const activeFeature = features[activeIndex];

  return (
    <section className="relative overflow-x-clip bg-background py-24 sm:py-32">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-0 top-1/4 h-96 w-96 rounded-full bg-brand-teal/5 blur-[140px]" />
        <div className="absolute bottom-1/4 right-0 h-96 w-96 rounded-full bg-violet-500/5 blur-[140px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* LEFT — sticky column */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mb-4 flex items-center gap-2"
              >
                <div className="h-px w-8 bg-gradient-to-r from-transparent to-brand-teal" />
                <span className="text-xs font-semibold uppercase tracking-wider text-brand-teal">
                  <Trans>Even more reasons to love Stylion</Trans>
                </span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="mb-6 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-[3.25rem] lg:leading-[1.1]"
              >
                <Trans>Stylion works smarter than any other AI resume builder.</Trans>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="mb-8 text-base leading-relaxed text-muted-foreground sm:text-lg"
              >
                <Trans>
                  Stylion makes it easy to get the help you need, stay organized, and take on
                  any challenge. Its clear, simple design just makes sense — especially if you
                  have many applications. Managing multiple resumes and keeping track of your
                  files is a breeze.
                </Trans>
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="mb-10"
              >
                <Button
                  asChild
                  size="lg"
                  className="bg-brand-teal text-black hover:bg-brand-teal/90"
                >
                  <Link to="/ai-generator">
                    <Trans>Create Free Resume</Trans>
                  </Link>
                </Button>
              </motion.div>

              {/* Scroll progress indicator — disabled when reduceMotion */}
              {!reduceMotion && (
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="hidden lg:block"
                >
                  <div className="mb-3 flex items-baseline justify-between gap-3">
                    <div className="overflow-hidden">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                        <Trans>Now showing</Trans>
                      </div>
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={activeFeature.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.25 }}
                          className="flex items-center gap-2 text-sm font-semibold text-white"
                        >
                          <span className="text-brand-teal">{activeFeature.icon}</span>
                          <span className="truncate">{activeFeature.title}</span>
                        </motion.div>
                      </AnimatePresence>
                    </div>
                    <div className="shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground/70">
                      {String(activeIndex + 1).padStart(2, "0")}
                      <span className="mx-0.5 text-white/20">/</span>
                      {String(features.length).padStart(2, "0")}
                    </div>
                  </div>

                  <div className="flex gap-1">
                    {features.map((feature, i) => {
                      const isActive = i === activeIndex;
                      const isPassed = i < activeIndex;
                      return (
                        <button
                          key={feature.id}
                          type="button"
                          aria-label={feature.title}
                          onClick={() => {
                            cardRefs.current[i]?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                          }}
                          className="group relative h-1.5 flex-1 overflow-hidden rounded-full bg-white/5 transition-all hover:bg-white/10"
                        >
                          <motion.span
                            className="absolute inset-0 origin-left rounded-full bg-gradient-to-r from-brand-teal to-brand-teal/60"
                            initial={false}
                            animate={{
                              scaleX: isActive ? 1 : isPassed ? 1 : 0,
                              opacity: isActive ? 1 : isPassed ? 0.4 : 0,
                            }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                          />
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          {/* RIGHT — scrolling grid */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {features.map((feature, i) => (
                <FeatureCard
                  key={feature.id}
                  feature={feature}
                  index={i}
                  isActive={!reduceMotion && activeIndex === i}
                  reduceMotion={reduceMotion}
                  registerRef={(el) => {
                    cardRefs.current[i] = el;
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
